import { House, MoreHorizontal, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "../lib/utils";

interface WorkspaceTab {
  path: string;
  href: string;
  label: string;
}

const STORAGE_KEY = "yky-crm-workspace-tabs-v1";
const homeTab: WorkspaceTab = { path: "/", href: "/", label: "总览" };

const routeLabels: Array<[RegExp, string]> = [
  [/^\/customers\/[^/]+$/, "客户资料"],
  [/^\/customers$/, "客户管理"],
  [/^\/key-customers$/, "重点客户"],
  [/^\/products(?:\/[^/]+)?$/, "产品档案"],
  [/^\/leads$/, "客户开发"],
  [/^\/samples\/[^/]+$/, "样品申请单"],
  [/^\/samples$/, "样品中心"],
  [/^\/opportunities$/, "报价商机"],
  [/^\/tasks$/, "待办提醒"],
  [/^\/whatsapp$/, "WhatsApp"],
  [/^\/email$/, "邮件中心"],
  [/^\/reports$/, "数据分析"],
  [/^\/imports$/, "Excel 导入"],
  [/^\/users$/, "账号权限"],
  [/^\/audit$/, "审计日志"],
  [/^\/settings$/, "系统设置"],
  [/^\/usage$/, "免费额度"],
];

function labelFor(path: string) {
  return routeLabels.find(([pattern]) => pattern.test(path))?.[1] || "业务页面";
}

function readTabs(): WorkspaceTab[] {
  if (typeof window === "undefined") return [homeTab];
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "[]") as WorkspaceTab[];
    const valid = stored.filter((tab) => tab && typeof tab.path === "string" && typeof tab.href === "string" && typeof tab.label === "string" && tab.path.startsWith("/"));
    return [homeTab, ...valid.filter((tab) => tab.path !== "/")].slice(0, 12);
  } catch {
    return [homeTab];
  }
}

export function WorkspaceTabs() {
  const location = useLocation();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDetailsElement>(null);
  const [tabs, setTabs] = useState(readTabs);
  const currentPath = location.pathname;
  const currentHref = `${location.pathname}${location.search}`;
  const currentLabel = useMemo(() => labelFor(currentPath), [currentPath]);

  useEffect(() => {
    setTabs((current) => {
      const nextTab = { path: currentPath, href: currentHref, label: currentLabel };
      const existing = current.findIndex((tab) => tab.path === currentPath);
      const next = existing >= 0
        ? current.map((tab, index) => index === existing ? nextTab : tab)
        : [...current, nextTab].slice(-12);
      const withHome = next.some((tab) => tab.path === "/") ? next : [homeTab, ...next].slice(0, 12);
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(withHome));
      return withHome;
    });
  }, [currentHref, currentLabel, currentPath]);

  function closeTab(path: string) {
    if (path === "/") return;
    const index = tabs.findIndex((tab) => tab.path === path);
    const next = tabs.filter((tab) => tab.path !== path);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setTabs(next);
    if (path === currentPath) {
      const fallback = tabs[index - 1] || tabs[index + 1] || homeTab;
      navigate(fallback.href);
    }
  }

  function closeOthers() {
    const active = tabs.find((tab) => tab.path === currentPath) || homeTab;
    const next = active.path === "/" ? [homeTab] : [homeTab, active];
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setTabs(next);
    menuRef.current?.removeAttribute("open");
  }

  return <div className="flex h-10 items-stretch border-b border-slate-200 bg-slate-100/80 px-2 sm:px-4">
    <div className="flex min-w-0 flex-1 items-end gap-1 overflow-x-auto scrollbar-thin">
      {tabs.map((tab) => {
        const active = tab.path === currentPath;
        return <div key={tab.path} className={cn("group flex h-9 shrink-0 items-center rounded-t-lg border px-2 text-xs transition", active ? "border-slate-200 border-b-white bg-white font-semibold text-blue-700" : "border-transparent text-slate-500 hover:bg-white/70 hover:text-slate-800")}>
          <button type="button" className="flex h-full items-center gap-1.5" onClick={() => navigate(tab.href)}>{tab.path === "/" ? <House className="size-3.5" /> : null}<span className="max-w-28 truncate">{tab.label}</span></button>
          {tab.path !== "/" ? <button type="button" aria-label={`关闭 ${tab.label}`} className="ml-1 rounded p-0.5 text-slate-300 hover:bg-slate-100 hover:text-slate-700" onClick={() => closeTab(tab.path)}><X className="size-3" /></button> : null}
        </div>;
      })}
    </div>
    <details ref={menuRef} className="relative ml-2 shrink-0 self-center">
      <summary aria-label="工作区标签操作" className="flex size-7 cursor-pointer list-none items-center justify-center rounded-md text-slate-500 hover:bg-white [&::-webkit-details-marker]:hidden"><MoreHorizontal className="size-4" /></summary>
      <div className="absolute right-0 top-8 z-50 w-36 rounded-lg border border-slate-200 bg-white p-1.5 shadow-xl"><button type="button" className="w-full rounded-md px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-100" onClick={closeOthers}>关闭其他标签</button></div>
    </details>
  </div>;
}
